/**
 * Concrete implementation of EditableLayerBase which serves as a wrapper around form responses.
 * @event ObjectLayer#child-moved
 * @description Fired when a child layer is moved from one index to another.
 * @param {ObjectLayer} parent A reference to the parent layer.
 * @param {ObjectLayer} layer The layer that was moved.
 * @param {Integer} oldIndex The previous index of the layer.
 * @param {Integer} newIndex The new index of the layer.
 *
 * @event ObjectLayer#child-added
 * @description Fired when a child layer is added to the layer.
 * @param {ObjectLayer} parent A reference to the parent layer.
 * @param {ObjectLayer} layer The layer that was added.
 * @param {Integer} index The index at which the new layer is located.
 *
 * @event ObjectLayer#child-removed
 * @description Fired when a child layer is removed from this layer.
 * @param {ObjectLayer} parent A reference to the parent layer.
 * @param {ObjectLayer} layer The layer that was removed.
 * @param {Integer} index The original index of the layer.
 *
 * @event ObjectLayer#name-field-changed
 * @description Fired when the nameField of the underlying model for this ObjectLayer changes.
 * @param {ObjectLayer} model A reference to this model.
 * @param {String} value The new value of the nameField.
 *
 * @module app/models/map/ObjectLayer
 */
import EditableLayerBase from "app/models/map/EditableLayerBase";
import _ from "underscore";
import SymbolFactory from "app/models/map/SymbolFactory";
import $ from "jquery";
import Feature from "app/models/Feature";
import Field from "core/models/Field";
import auth from "app/auth";
import ModelUtils from "@HBKEngineering/model-util";
import Document from "app/models/Document";
import session from "app/Session";
import urlService from "app/UrlService";
var ObjectLayer = EditableLayerBase.extend({
  /**
   * Initializes this object.
   * @param  {object} options The options hash.
   * @param {ObjectLayer} [options.parent] A reference to the parent layer, if any.
   * @param {Layer} options.featureLayer The form which specifies the fields for the objects in this layer.
   * @param {FeatureCollection} options.features The features to display in this layer.
   * @param {InternalMapService} options.serviceLayer The map service to associated with this layer.
   */
  initialize: function (options) {
    EditableLayerBase.prototype.initialize.apply(this, arguments);

    //options should contain a "form" and a "parent" (of type ObjectLayer) where applicable

    /**
     * The parent layer, if any.
     * @type {ObjectLayer}
     */
    this.parent = options.parent;
    /**
     * The layer model associated with this layer.
     * @type {app/models/Layer}
     */
    this.featureLayer = options.featureLayer;

    this.listenTo(this.featureLayer, "change:nameField", function () {
      this.trigger(
        "name-field-changed",
        this,
        this.featureLayer.get("nameField")
      );
    });

    if (_.isObject(this.featureLayer.get("appearance"))) {
      var appearance = this.featureLayer.get("appearance");

      this.setSymbolFactory(new SymbolFactory(appearance));
    }

    /**
     * The features associated with this layer.
     * @type {app/models/FeatureCollection}
     */
    this.features = options.features;
    /**
     * The internal map service associated with this layer
     * @type {InternalMapService}
     */
    this.serviceLayer = options.serviceLayer;

    this._children = options.children || [];
    this._loaded = false;
  },

  canDraw: function () {
    return this.featureLayer.allows(session, "add");
  },

  isEditable: function () {
    return this.featureLayer.allows(session, "modify");
  },

  createMapLayer: function () {
    var GraphicsLayer = window.require("esri/layers/GraphicsLayer");
    var opacity = this.featureLayer.get("opacity");
    var self = this;

    if (!_.isNumber(opacity)) {
      opacity = 1;
    }

    var graphicsLayer = new GraphicsLayer({
      opacity: opacity,
    });

    var augmentLayer = function () {
      var displayNameField = self.getDisplayNameField();

      if (displayNameField) {
        graphicsLayer.showLabels = self.featureLayer.get("showLabels"); // maybe someday we can update everything if showLabels changes
        graphicsLayer.fields = [
          {
            name: displayNameField.id,
            type: "esriFieldTypeString",
          },
        ];
      }

      return !!displayNameField;
    };

    if (!augmentLayer()) {
      this.featureLayer.getFields().once("sync", augmentLayer);
    }

    return graphicsLayer;
  },

  reload: function () {
    this.clear();
    delete this._loaded;

    return this.load();
  },

  load: function () {
    var promise = EditableLayerBase.prototype.load.apply(this, arguments);
    var self = this;

    if (!self._loaded) {
      self._loaded = promise.then(function (self, layer) {
        var deferred = new $.Deferred();

        self.featureLayer
          .getFields()
          .fetch({
            reset: true,
            success: function () {
              self.features
                .fetch({
                  reset: true,
                  success: function (features) {
                    self.clear();
                    _.each(features.models, function (model) {
                      if (
                        model.getGeometry().get("type") &&
                        model.getGeometry().getCoordinateArrayCount()
                      ) {
                        self.add(model.getGeometry(), model.getProperties());
                      } else {
                        console.debug(
                          "Encountered geometry with no type or no coordinates"
                        );
                      }
                    });
                    self._loaded = deferred;
                    deferred.resolve(self, layer);
                  },
                })
                .fail(function (xhr) {
                  self._loaded = deferred;
                  deferred.reject(xhr, self, layer);
                });
            },
          })
          .fail(function (xhr) {
            self._loaded = deferred;
            deferred.reject(xhr, self, layer);
          });

        return deferred;
      });
    }

    return self._loaded;
  },

  /**
   * Creates a data model to associate with the given geometry.
   * @param  {app/models/map/Geometry} geometryModel The geometry model.
   * @return {object}               The data model.
   */
  createDataModel: function (geometryModel) {
    var feature =
      geometryModel.getFeature() ||
      Feature.generate(geometryModel, {
        featureLayer: this.featureLayer,
      });

    return feature.getProperties();
  },

  /**
   * Adds an entry to this layer.
   * @param {app/models/map/Geometry} geometryModel The geometry model.
   * @param {object} [dataModel]     The data model.
   */
  addObject: function (geometryModel, dataModel) {
    dataModel = EditableLayerBase.prototype.addObject.apply(this, arguments);

    dataModel.getFeature().set("dataType", this.featureLayer.id);

    return dataModel;
  },

  getFields: function () {
    var parentFields = [];

    if (this.parent) {
      parentFields = this.parent.getFields(); //this is an array
    }

    this._fields = [].concat(
      parentFields,
      _.values(this.featureLayer.createFields())
    );

    return this._fields;
  },

  getIdField: function () {
    if (!this._idField) {
      this._idField = new Field({
        id: "featureId",
        name: "featureId",
        label: "ID",
      });
    }

    return this._idField;
  },

  getId: function (model) {
    return model.getFeature().id;
  },

  getDisplayNameField: function () {
    var fields = this.getFields();
    var nameField = this.featureLayer.get("nameField");
    if (nameField) {
      this._displayField = _.find(
        fields,
        function (field) {
          return field.id == nameField;
        },
        this
      );
    } else if (fields.length) {
      this._displayField = _.first(
        _.filter(
          fields,
          function (field) {
            return field.get("editor") === "text";
          },
          this
        )
      );
    } else {
      this._displayField =
        EditableLayerBase.prototype.getDisplayNameField.apply(this, arguments);
    }

    return this._displayField;
  },

  getDisplayName: function (model) {
    if (this.getDisplayNameField()) {
      return this.getDisplayNameField().getValue(model);
    } else {
      return null;
    }
  },

  getChildren: function () {
    return this._children;
  },

  getName: function () {
    return this.featureLayer.get("name");
  },

  hasFiles: function () {
    return this.featureLayer.hasFiles();
  },

  getRootFilePath: function (model) {
    return this.hasFiles() ? Feature.getDocumentsUrl(model.id) : null;
  },

  getChildDirectoryPath: function (folderModel) {
    return this.hasFiles() ? folderModel.getChildrenUrl() : null;
  },

  getChildFilePath: function (fileModel) {
    var result = null;

    if (this.hasFiles()) {
      var feature = fileModel.get("item");

      if (feature) {
        result = Feature.getDocumentFilePath(
          ModelUtils.idFor(feature),
          fileModel,
          auth.token
        );
      } else {
        result = fileModel.getFilePath(auth.token);
      }
    }

    return result;
  },

  loadFiles: function () {
    var deferred = new $.Deferred();
    var documents = this.featureLayer.getDocuments();
    var self = this;

    documents.fetch({
      success: function () {
        var documentMap = {};
        var results = {};

        results[self.featureLayer.id] = documentMap;

        documents.forEach(function (_document) {
          var featureId = ModelUtils.idFor(_document.get("item"));
          var col = documentMap[featureId];

          if (!col) {
            col = documentMap[featureId] = Feature.getDocuments(featureId);
          }

          col.add(_document);
        });

        deferred.resolve(results);
      },
      error: function (collection, response) {
        deferred.reject(response);
      },
    });

    return deferred.promise();
  },

  loadModel: function (modelId) {
    var deferred = new $.Deferred();

    deferred.resolve(this.features.get(modelId));

    return deferred.promise();
  },

  summarize: function () {
    if (this.serviceLayer && this.serviceLayer.summarizer) {
      return this.serviceLayer.summarizer.summarizeLayer(this);
    } else {
      return {
        count: 0,
        summary_fields: [],
      };
    }
  },

  /**
   * Updates the position attributes of all child layers.
   * @private
   */
  _updateIndicies: function () {
    _.each(this.getChildren(), function (item, index) {
      item.featureLayer.set("position", index);
    });
  },

  /**
   * Move the child layer at the given old index to the given new index. If there are no children or the index is invalid, this function does nothing. The moved layer will have its `position` attribute updated appropriately.
   * @fires ObjectLayer#child-moved
   * @param {Integer} oldIndex The current index of the layer to move.
   * @param {Integer} newIndex The new index at which to place the layer.
   */
  moveChildLayer: function (oldIndex, newIndex) {
    if (this.getChildren()) {
      if (oldIndex >= 0 && oldIndex < this.getChildren().length) {
        var result = this.getChildren().splice(oldIndex, 1);

        this.getChildren().splice(newIndex, 0, result[0]);

        this._updateIndicies();

        this.trigger("child-moved", this, result[0], oldIndex, newIndex);
      }
    }
  },

  /**
   * Adds a child layer at the end of the list of children.
   * @fires ObjectLayer#child-added
   * @param {ObjectLayer} layer The layer to add. `layer.featureLayer` will have its position, parent, and ownerService attributes updated appropriately.
   * @param {Integer} index The index at which to add the layer. By default, child layers are added at the end of the list.
   */
  addChildLayer: function (layer, index) {
    if (this.getChildren()) {
      if (!_.isNumber(index)) {
        index = this.getChildren().length;
      }

      this.getChildren().splice(index, 0, layer);
    }
    layer.featureLayer.set({
      service: this.featureLayer.get("service"),
      parent: this.featureLayer.id,
    });

    this._updateIndicies();

    this.trigger("child-added", this, layer, index);
  },

  /**
   * Removes a child layer from the list of children. This does *not* destroy the layer in the database.
   * @fires ObjectLayer#child-removed
   * @param {ObjectLayer|Integer} layer The layer to remove, or its index. If the index is invalid, no changes will be made.
   * @return {ObjectLayer} The layer that was removed, if any.
   */
  removeChildLayer: function (layer) {
    if (this.getChildren()) {
      if (!_.isNumber(layer)) {
        layer = this.getChildren().indexOf(layer);
      }

      if (layer >= 0 && layer < this.getChildren().length) {
        var result = this.getChildren().splice(layer, 1);

        this.trigger("child-removed", this, result[0], layer); //reminder that layer is actually the index of the layer

        return result[0];
      }
    }
  },

  getWrappedModel: function () {
    return this.featureLayer;
  },

  /**
   * Gets a value indicating whether or not this layer supports exporting of data.
   * @returns {Boolean} True if export is supported, otherwise false.
   */
  canExport: function () {
    return true;
  },
});

export default ObjectLayer;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/map/ObjectLayer.js
// module id = 356
// module chunks = 1